import type {ReactElement} from 'react';
import React, {useEffect, useRef, useState, useMemo} from 'react';
import type {Block} from 'grapesjs';
import type {BlockCategoryMap, CategoryPill} from '@Components/email-maker-block-manager/email-maker-block-manager.types';
import {type TabItemContentStorage, Tabs} from '@Components/tabs';
import type PresetComponentVO from '@/email-maker/grapesjs-email-editor-plugin/src/vos/presetcompvo';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {PillButton} from '@Components/pill-button';
import {getUniqueString} from '@Utils/string.util';
import {PillButtonSize} from '@Components/pill-button/pill-button.types';
import {Pill, PILL_CONTENT_TYPE, PILL_SIZE} from '@Components/pill';
import {EmailMakerBlock} from '@Components/email-maker-block-manager/components/email-maker-block';
import {useHasUserRecentlySavedTemplate, useSortedCategories} from '@Components/email-maker-block-manager/email-maker-block-manager.hooks';
import {EmailComponentTemplates} from '@Components/email-maker-block-manager/components/email-component-templates/email-component-templates';
import {getAllCategoriesPill} from '@Libraries/email-component-template-library';
import {useSelectedTab} from '@Components/tabs';
import {UserEmailSections} from '@Components/email-maker-block-manager/components/user-email-sections';
import {useAppDispatch} from '@/hooks';
import {noop} from '@Utils/general.util';
import {useGrapesjsEditor, useIsMobileScreen} from '@Components/email-maker-block-manager/email-maker-block-manager-grapesjs-editor-context';
import {MobilePanelHeader} from '@Components/email-maker-block-manager/components/mobile-panel-header';
import {selectTab} from '@Components/tabs/tabs-reducer';
import {setRecentlySavedUserTemplate} from '@Components/email-maker-block-manager/email-maker-block-manager.reducer';
import {EMAILMAKER_ADD_SECTION_BLANK_SECTIONS, GA4EventName, GA4EventParamName} from '@Libraries/ga-events';
import styles from './sections-block-panel.module.scss';

interface SectionsBlockPanelProps {
  blocksMap: BlockCategoryMap;
  showBackButton?: boolean;
  headerLabel?: string;
  onBackButton?: VoidFunction;
}

enum Panel {
  TEMPLATES = 'templates',
  BLANK_SECTIONS = 'blank-sections',
  SAVED = 'saved',
}

enum DIRECTION {
  LEFT = -1,
  RIGHT = 1,
}

const PILL_SCROLL_WIDTH = 300;
const TAB_ID = 'email_sections_block_tab';

export function SectionsBlockPanel({blocksMap, showBackButton = false, headerLabel = '', onBackButton = noop}: SectionsBlockPanelProps): ReactElement {
  const BLOCK_CATEGORY = window.PMW.emails.blocks.CATEGORY_SECTIONS;
  const ALL_CATEGORIES_PILL = getAllCategoriesPill();
  const dispatch = useAppDispatch();
  const allSortedCategories = useSortedCategories();
  const gjsEditor = useGrapesjsEditor();
  const isMobileScreen = useIsMobileScreen();
  const hasUserRecentlySavedTemplate = useHasUserRecentlySavedTemplate();

  const [currentPanel, setCurrentPanel] = useState<Panel>(useSelectedTab(TAB_ID, Panel.TEMPLATES) as Panel);
  const [selectedPill, setSelectedPill] = useState<CategoryPill>(ALL_CATEGORIES_PILL);
  const [showPillScrollLeft, setShowPillScrollLeft] = useState<boolean>(false);
  const [showPillScrollRight, setShowPillScrollRight] = useState<boolean>(false);
  const [showPanelHeaderShadow, setShowPanelHeaderShadow] = useState<boolean>(false);

  const pillsContainerRef = useRef<HTMLDivElement>(null);
  const templatesContainerRef = useRef<HTMLDivElement>(null);
  const blankSectionsContainerRef = useRef<HTMLDivElement>(null);
  const savedSectionsContainerRef = useRef<HTMLDivElement>(null);

  const getTabItems = (): TabItemContentStorage[] => {
    const textClasses = `${styles.tabHeading} spacing-p-t-2 spacing-p-b-2 spacing-p-l-0 spacing-p-r-0`;
    return [
      {
        tabKey: Panel.TEMPLATES,
        text: window.i18next.t('pmwjs_templates'),
        textClassName: textClasses,
      },
      {
        tabKey: Panel.BLANK_SECTIONS,
        text: window.i18next.t('pmwjs_blank_sections'),
        textClassName: textClasses,
      },
      {
        tabKey: '',
        disableSelection: true,
        text: '',
        containerClassName: `spacing-m-0`,
        className: `spacing-p-0 ${styles.divider}`,
      },
      {
        tabKey: Panel.SAVED,
        text: window.i18next.t('pmwjs_saved'),
        textClassName: textClasses,
        containerClassName: `spacing-m-l-5`,
        preTitleContent: <Icon icon="icon-bookmark" size={IconSize.SIZE_ICON_16} className="spacing-m-r-1" />,
      },
    ];
  };

  const showPillScrollIfNeeded = (): void => {
    const pillsContainer = pillsContainerRef.current;

    if (!pillsContainer) {
      return;
    }

    const {scrollLeft, scrollWidth, clientWidth} = pillsContainerRef.current;
    const scrollRight = scrollWidth - scrollLeft - clientWidth;

    const scrollLeftShown = scrollLeft >= 1;
    const scrollRightShown = scrollRight >= 1;

    setShowPillScrollLeft(scrollLeftShown);
    setShowPillScrollRight(scrollRightShown);
  };

  const handlePillScroll = (direction: DIRECTION): void => {
    pillsContainerRef.current?.scrollBy({
      left: direction * PILL_SCROLL_WIDTH,
      behavior: 'smooth',
    });
  };

  const getPillScrollArrows = (): ReactElement => {
    const classNames = [`${styles.scrollLeft} ${showPillScrollLeft ? '' : '_hidden'}`, `${styles.scrollRight} ${showPillScrollRight ? '' : '_hidden'}`];
    const icons = ['icon-caret-left', 'icon-caret-right'];
    const directions = [DIRECTION.LEFT, DIRECTION.RIGHT];

    const scrollArrows = classNames.map((className, index) => {
      return (
        <div key={getUniqueString()} className={`${styles.pillScrollContainer} ${className}`}>
          <Pill
            key={getUniqueString()}
            className={`${styles.pill} ${styles.pillScroll}`}
            content={{
              type: PILL_CONTENT_TYPE.ICON,
              icon: icons[index],
              size: IconSize.SIZE_ICON_16,
            }}
            size={PILL_SIZE.MEDIUM}
            onClick={() => {
              handlePillScroll(directions[index]);
            }}
          />
        </div>
      );
    });

    return <>{scrollArrows}</>;
  };

  const getOrderedSectionCategoryPills = (): CategoryPill[] => {
    const pillsToMoveToEnd = allSortedCategories.slice(0, 2);
    const pillsToMoveToFront = allSortedCategories.slice(2);

    return [ALL_CATEGORIES_PILL, ...pillsToMoveToFront, ...pillsToMoveToEnd];
  };

  const getPills = (): ReactElement | null => {
    if (currentPanel !== Panel.TEMPLATES) {
      return null;
    }

    const sectionCategories = getOrderedSectionCategoryPills();

    const clickHandler = (category: CategoryPill): void => {
      setSelectedPill(category);
      templatesContainerRef.current?.scrollTo({top: 0, behavior: 'instant'});

      if (category.id !== ALL_CATEGORIES_PILL.id) {
        window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EMAILMAKER_SECTION_SELECT, {[GA4EventParamName.TYPE]: category.trackingName});
      }
    };

    const pills = sectionCategories.map((category) => {
      return (
        <PillButton
          key={getUniqueString()}
          className={styles.pill}
          text={category.name}
          isSelected={selectedPill?.id === category.id}
          size={PillButtonSize.SMALL}
          onClick={() => {
            clickHandler(category);
          }}
        />
      );
    });

    return (
      <div ref={pillsContainerRef} className={styles.pillsContainer}>
        {pills}
        {getPillScrollArrows()}
      </div>
    );
  };

  const getBlankSectionVisual = (block: Block): ReactElement => {
    const presets = gjsEditor.runCommand(window.PMW.emails.commands.GET_SECTION_COLUMNS_PRESETS) as PresetComponentVO[];
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const appliedPreset = presets.find((preset) => preset.id === block.getContent()[window.PMW.emails.props.COLUMNS_PRESET]);

    const columnWidths = appliedPreset?.compValues;
    const widthUnit = appliedPreset?.classUnit;

    if (!columnWidths || !widthUnit) {
      return <div>Column widths or width unit not defined</div>;
    }

    return (
      <>
        {columnWidths.map((width) => {
          return <div className={styles.blankSectionElement} style={isMobileScreen ? {height: `${width}${widthUnit}`} : {width: `${width}${widthUnit}`}} key={getUniqueString()} />;
        })}
      </>
    );
  };

  const getBlankSectionBlocks = (): ReactElement => {
    return (
      <div className={`${styles.blankSectionBlocks} `}>
        {blocksMap[BLOCK_CATEGORY].map((block) => {
          return (
            <EmailMakerBlock block={block} extraClasses={styles.block} key={getUniqueString()}>
              {getBlankSectionVisual(block)}
            </EmailMakerBlock>
          );
        })}
      </div>
    );
  };

  const getBlankSectionsPanel = (): ReactElement => {
    return (
      <div ref={blankSectionsContainerRef} className={`${styles.panelContainer} ${styles.blankSectionsContainer}`}>
        {getBlankSectionBlocks()}
      </div>
    );
  };

  const getTemplatesSectionPanel = (): ReactElement => {
    return (
      <div ref={templatesContainerRef} className={`${styles.panelContainer} ${styles.templatesContainer}`}>
        <EmailComponentTemplates selectedCategoryId={selectedPill.id} />
      </div>
    );
  };

  const getSavedSectionsPanel = (): ReactElement => {
    return (
      <div ref={savedSectionsContainerRef} className={`${styles.panelContainer} ${styles.savedSectionContainer}`}>
        <UserEmailSections />
      </div>
    );
  };

  const getCurrentPanel = (): ReactElement | null => {
    switch (currentPanel) {
      case Panel.TEMPLATES:
        return getTemplatesSectionPanel();
      case Panel.BLANK_SECTIONS:
        return getBlankSectionsPanel();
      case Panel.SAVED:
        return getSavedSectionsPanel();
      default:
        return null;
    }
  };

  const getCurrentPanelMemoized = useMemo(() => getCurrentPanel(), [currentPanel, selectedPill]);

  const showPanelHeaderShadowIfNeeded = (): void => {
    const panelRefMap = {
      [Panel.TEMPLATES]: templatesContainerRef,
      [Panel.BLANK_SECTIONS]: blankSectionsContainerRef,
      [Panel.SAVED]: savedSectionsContainerRef,
    };

    setShowPanelHeaderShadow((panelRefMap[currentPanel].current?.scrollTop ?? 0) > 0);
  };

  useEffect(() => {
    if (hasUserRecentlySavedTemplate) {
      dispatch(setRecentlySavedUserTemplate(null));
      dispatch(selectTab(TAB_ID, Panel.SAVED));
      setCurrentPanel(Panel.SAVED as Panel);

      if (isMobileScreen) {
        gjsEditor.runCommand(window.PMW.emails.commands.OPEN_ADD_BLOCKS_DIALOG);
      }
    }
  }, [hasUserRecentlySavedTemplate]);

  useEffect(() => {
    return (): void => {
      dispatch(selectTab(TAB_ID, Panel.TEMPLATES));
    };
  }, []);

  useEffect(() => {
    showPillScrollIfNeeded();
    pillsContainerRef.current?.addEventListener('scroll', showPillScrollIfNeeded, {passive: true});
    blankSectionsContainerRef.current?.addEventListener('scroll', showPanelHeaderShadowIfNeeded, {passive: true});
    templatesContainerRef.current?.addEventListener('scroll', showPanelHeaderShadowIfNeeded, {passive: true});
    savedSectionsContainerRef.current?.addEventListener('scroll', showPanelHeaderShadowIfNeeded, {passive: true});

    blankSectionsContainerRef.current?.scrollTo({top: 0, behavior: 'instant'});
    templatesContainerRef.current?.scrollTo({top: 0, behavior: 'instant'});
    savedSectionsContainerRef.current?.scrollTo({top: 0, behavior: 'instant'});

    return (): void => {
      pillsContainerRef.current?.removeEventListener('scroll', showPillScrollIfNeeded);
      blankSectionsContainerRef.current?.removeEventListener('scroll', showPanelHeaderShadowIfNeeded);
      templatesContainerRef.current?.removeEventListener('scroll', showPanelHeaderShadowIfNeeded);
      savedSectionsContainerRef.current?.removeEventListener('scroll', showPanelHeaderShadowIfNeeded);
    };
  }, [currentPanel]);

  const getMobilePanelHeader = (): ReactElement | null => {
    if (!isMobileScreen || !headerLabel) {
      return null;
    }

    return (
      <div className={styles.mobilePanelHeader}>
        <MobilePanelHeader headerLabel={headerLabel} showHandleBar showBackButton={showBackButton} onBackButton={onBackButton} />
      </div>
    );
  };

  const tabClickHandler = (tabKey: string): void => {
    const clickedPanel = tabKey as Panel;

    if (Object.values(Panel).includes(clickedPanel)) {
      setCurrentPanel(tabKey as Panel);
    }

    if (clickedPanel === Panel.BLANK_SECTIONS) {
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EMAILMAKER_SECTION_SELECT, {[GA4EventParamName.TYPE]: EMAILMAKER_ADD_SECTION_BLANK_SECTIONS});
    }
  };

  return (
    <>
      <div className={`spacing-p-t-2 ${styles.panelHeader} ${showPanelHeaderShadow ? styles.shadow : ''}`}>
        {getMobilePanelHeader()}
        <Tabs id={TAB_ID} tabItems={getTabItems()} selectedTab={currentPanel} className={styles.tabsContainer} onTabClick={tabClickHandler} />
        {getPills()}
      </div>
      {getCurrentPanelMemoized}
    </>
  );
}
